import { CircleDollarSign, CheckCircle2, Clock3, Users, Briefcase, AlertTriangle, TrendingUp } from "lucide-react";
import { Spin, Tag, Alert, Button } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import DashboardCard from "../../components/reusableCompNew/Dashboard/DashboardCard";
import KPIStatCard from "../../components/reusableCompNew/Dashboard/StatCard";
import RevenueChart from "../../components/reusableCompNew/Dashboard/RevenueChart";
import StatusChart from "../../components/reusableCompNew/Dashboard/StatusChart";
import { useUserStore } from "../../../store/slices/user";
import { useDashboardDetails } from "../../../hooks/api/dashboard/hooks";
import { useTerminology } from "../../../lib/terminology";
import { KPIItem, StatusData, RevenueData } from "../../../types/api/dashboard";
import { ExpiringAssetsAlert } from "../../../components/ExpiringAssetsAlert";
import { JOB_STATUS_TAG_CFG } from "../../../constants/jobStatusConfig";
import { SEMANTIC_HEX } from "../../../constants/statusColors";

function completionColor(rate: number): string {
  if (rate >= 80) return "text-emerald-600";
  if (rate >= 50) return "text-amber-500";
  return "text-red-500";
}

function CompletionRateCard({
  rate,
  completed,
  scheduled,
  onClick,
}: {
  rate?: number;
  completed?: number;
  scheduled?: number;
  onClick?: () => void;
}) {
  const hasData = (scheduled ?? 0) > 0;
  const valueColor = hasData ? completionColor(rate ?? 0) : "text-slate-800";
  return (
    <DashboardCard className="p-5 hover:-translate-y-0.5 transition-all duration-150" onClick={onClick}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">
          Today&apos;s Completion
        </span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-teal-50">
          <div className="text-teal-600"><TrendingUp size={18} /></div>
        </div>
      </div>
      <div className={`text-2xl font-bold ${valueColor}`}>
        {hasData ? `${rate ?? 0}%` : "—"}
      </div>
      <div className="mt-1">
        <span className="text-xs font-medium text-slate-500">
          {completed ?? 0} of {scheduled ?? 0} jobs
        </span>
      </div>
    </DashboardCard>
  );
}

const DashboardPage = () => {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const { term, industryName } = useTerminology();
  const { data: statsRes, isLoading, isError, error } = useDashboardDetails();
  const stats = statsRes?.data;

  const kpiCards: KPIItem[] = [
    {
      title: `Total ${term("invoice", true)}`,
      value: stats ? `£${Number(stats.invoices.total_paid).toLocaleString("en-GB", { minimumFractionDigits: 0 })}` : "—",
      change: stats ? `${stats.invoices.total} invoices total` : "Loading…",
      type: "positive",
      icon: <CircleDollarSign size={18} />,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      onClick: () => navigate("/invoices"),
    },
    {
      title: `${term("job", true)} Completed`,
      value: stats ? stats.jobs.completed : "—",
      change: stats ? `${stats.jobs.in_progress} in progress` : "Loading…",
      type: "positive",
      icon: <CheckCircle2 size={18} />,
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
      onClick: () => navigate("/jobs?status=completed"),
    },
    {
      title: "Outstanding",
      value: stats ? `£${Number(stats.invoices.outstanding).toLocaleString("en-GB", { minimumFractionDigits: 0 })}` : "—",
      change: stats ? `${stats.invoices.overdue_count} overdue ${term("invoice", true).toLowerCase()}` : "Loading…",
      type: stats?.invoices.overdue_count ? "negative" : "neutral",
      icon: <Clock3 size={18} />,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
      onClick: () => navigate("/invoices?status=overdue"),
    },
    {
      title: `Active ${term("worker", true)}`,
      value: stats ? `${stats.workers.active} / ${stats.workers.total}` : "—",
      change: stats ? `${stats.workers.total - stats.workers.active} unavailable` : "Loading…",
      type: "neutral",
      icon: <Users size={18} />,
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
      onClick: () => navigate("/field-workers"),
    },
  ];

  const statusData: StatusData[] = stats
    ? [
        { name: "In Progress", value: stats.jobs.in_progress, color: SEMANTIC_HEX.info },
        { name: "Completed",   value: stats.jobs.completed,   color: SEMANTIC_HEX.success },
        { name: "Scheduled",   value: stats.jobs.scheduled,   color: SEMANTIC_HEX.warning },
        { name: "Draft",       value: stats.jobs.draft,       color: SEMANTIC_HEX.neutral },
        { name: "Cancelled",   value: stats.jobs.cancelled,   color: SEMANTIC_HEX.danger },
      ].filter((s) => s.value > 0)
    : [];

  const revenueData: RevenueData[] = stats?.revenue_by_month.map((r) => ({
    month: r.month,
    revenue: r.revenue,
    target: 0,
  })) ?? [];

  return (
    <div className="min-h-screen">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">
            Good {getGreeting()}{user?.first_name ? `, ${user.first_name}` : user?.name && !user.name.includes("undefined") ? `, ${user.name}` : ""} 👋
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {dayjs().format("dddd, D MMMM YYYY")}
            {industryName ? ` — ${industryName}` : ""}
          </p>
        </div>
      </div>

      {/* ERROR */}
      {!isLoading && isError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load dashboard"
            description={(error as any)?.response?.data?.message ?? (error as any)?.message ?? "Please refresh the page or contact support."}
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spin size="large" />
        </div>
      ) : isError ? null : (
        <>
          {/* KPI CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-4">
            {kpiCards.map((item) => (
              <KPIStatCard key={item.title as string} item={item} />
            ))}
            <CompletionRateCard
              rate={stats?.today?.completion_rate ?? undefined}
              completed={stats?.today?.completed}
              scheduled={stats?.today?.scheduled}
              onClick={() => navigate("/jobs")}
            />
          </div>

          {/* EXPIRING ASSETS ALERT */}
          <ExpiringAssetsAlert />

          {/* OVERDUE JOBS ALERT — the API already computes this queue (jobs
              still "scheduled" more than 15 minutes past their start time)
              but it was being silently dropped: nothing in this page ever
              read stats.overdue_jobs, so an owner opening the dashboard had
              no way to see that jobs were stuck unstarted. */}
          {stats?.overdue_jobs && stats.overdue_jobs.length > 0 && (
            <div className="mb-6">
              <Alert
                type="error"
                showIcon
                message={`${stats.overdue_jobs.length} ${term("job", stats.overdue_jobs.length !== 1).toLowerCase()} overdue — not started past their scheduled time`}
                description={stats.overdue_jobs
                  .slice(0, 5)
                  .map((j) => `${j.job_number} (${j.client_name}, ${j.assigned_worker}) — ${Math.floor(j.minutes_overdue / 60)}h overdue`)
                  .join(" · ")}
                action={
                  <Button size="small" danger onClick={() => navigate("/jobs?status=scheduled")}>
                    Review
                  </Button>
                }
              />
            </div>
          )}

          {/* UNASSIGNED JOBS ALERT — same story as above: the API already
              counts draft/scheduled jobs with no worker on them, this just
              never rendered anywhere. */}
          {!!stats?.unassigned_count && stats.unassigned_count > 0 && (
            <div className="mb-6">
              <Alert
                type="warning"
                showIcon
                message={`${stats.unassigned_count} ${term("job", stats.unassigned_count !== 1).toLowerCase()} waiting for a ${term("worker").toLowerCase()} to be assigned`}
                action={
                  <Button size="small" onClick={() => navigate("/jobs?status=scheduled")}>
                    Assign now
                  </Button>
                }
              />
            </div>
          )}

          {/* LOW STOCK ALERT */}
          {stats?.low_stock_items && stats.low_stock_items.length > 0 && (
            <div className="mb-6">
              <Alert
                type="warning"
                showIcon
                message={`${stats.low_stock_items.length} item(s) low on stock`}
                description={
                  stats.low_stock_items
                    .slice(0, 5)
                    .map((i) => `${i.name} (${i.stock_quantity} left)`)
                    .join(", ")
                }
                action={
                  <Button size="small" onClick={() => navigate("/inventory")}>
                    View Inventory
                  </Button>
                }
              />
            </div>
          )}

          {/* CHARTS */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            <DashboardCard className="md:col-span-2 xl:col-span-2 p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-semibold text-slate-700">Revenue Overview</h3>
              </div>
              {revenueData.length > 0 ? (
                <RevenueChart revenueData={revenueData} />
              ) : (
                <div className="flex items-center justify-center h-40 text-slate-400 text-sm">No revenue data yet</div>
              )}
            </DashboardCard>

            <DashboardCard className="p-5">
              <h3 className="font-semibold text-slate-700 mb-4">{term("job", true)} by Status</h3>
              {statusData.length > 0 ? (
                <>
                  <StatusChart statusData={statusData} />
                  <div className="mt-4 space-y-2">
                    {statusData.map((item) => (
                      <div key={item.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                          {item.name}
                        </div>
                        <span className="font-semibold">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-40 text-slate-400 text-sm">No {term("job", true).toLowerCase()} yet</div>
              )}
            </DashboardCard>
          </div>

          {/* BOTTOM */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* RECENT JOBS */}
            <DashboardCard className="md:col-span-2 xl:col-span-2 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-semibold text-slate-700">Recent {term("job", true)}</h3>
              </div>
              {stats?.recent_jobs.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-xs">
                        <th className="text-left px-5 py-3 font-medium">{term("job")}</th>
                        <th className="text-left py-3 font-medium">{term("client")}</th>
                        <th className="text-left py-3 font-medium">{term("worker")}</th>
                        <th className="text-left py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_jobs.map((job) => (
                        <tr key={job.id} className="border-t border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/jobs?jobId=${job.id}`)}>
                          <td className="px-5 py-3">
                            <div className="font-medium text-slate-800 text-xs">{job.job_number}</div>
                            <div className="text-xs text-slate-400 line-clamp-1">{job.title}</div>
                          </td>
                          <td className="py-3 text-slate-600 text-xs">{job.client_name}</td>
                          <td className="py-3 text-slate-600 text-xs">{job.worker_name}</td>
                          <td className="py-3">
                            <Tag color={(JOB_STATUS_TAG_CFG[job.status] ?? JOB_STATUS_TAG_CFG["draft"]).cls} className="text-[10px]">
                              {(JOB_STATUS_TAG_CFG[job.status] ?? JOB_STATUS_TAG_CFG["draft"]).label}
                            </Tag>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Briefcase size={28} className="mb-2 text-slate-300" />
                  <p className="text-sm">No {term("job", true).toLowerCase()} yet</p>
                </div>
              )}
            </DashboardCard>

            {/* JOB SUMMARY CARD */}
            <DashboardCard className="p-5">
              <h3 className="font-semibold text-slate-700 mb-4">{term("job")} Summary</h3>
              {stats ? (
                <div className="space-y-3">
                  {[
                    { label: `Total ${term("job", true)}`, value: stats.jobs.total, color: "text-slate-700" },
                    { label: "Scheduled", value: stats.jobs.scheduled, color: "text-amber-600" },
                    { label: "In Progress", value: stats.jobs.in_progress, color: "text-blue-600" },
                    { label: "Completed", value: stats.jobs.completed, color: "text-emerald-600" },
                    { label: "Cancelled", value: stats.jobs.cancelled, color: "text-red-500" },
                  ].map((s) => (
                    <div key={s.label} className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">{s.label}</span>
                      <span className={`font-semibold ${s.color}`}>{s.value}</span>
                    </div>
                  ))}
                  {stats.invoices.overdue_count > 0 && (
                    <div className="mt-3 bg-red-50 border border-red-100 rounded-lg p-3 flex gap-2 items-start">
                      <AlertTriangle size={14} className="text-red-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-red-600">
                        {stats.invoices.overdue_count} overdue {term("invoice", stats.invoices.overdue_count !== 1).toLowerCase()} — £{Number(stats.invoices.outstanding).toLocaleString("en-GB")} outstanding
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-slate-400 text-sm">No data</div>
              )}
            </DashboardCard>
          </div>
        </>
      )}
    </div>
  );
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

export default DashboardPage;
